import React, { useMemo, useRef } from "react";
import { BsFillCaretDownFill, BsFillCaretUpFill } from "react-icons/bs";
import { getCurrentDate, minutesToHHMM } from "../../../../Utilitie";
import DownloadExcel from "../../../../Extra/DownloadExcel";

const PDDSummaryTable = ({ data }) => {
  const monthOrder = [
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];
  const tableRef = useRef();

  // const handleExport = (ref, tableName) => {
  //   exportTableToExcel(ref.current, tableName);
  // };
  // Convert time string (HH:MM:SS) to seconds for calculations
  const timeToSeconds = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return 0;
    const [hours = 0, minutes = 0, seconds = 0] = timeStr
      .split(":")
      .map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  // Convert seconds to HH:MM:SS format
  const secondsToHHMMSS = (totalSeconds) => {
    if (!totalSeconds) return "--";
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.round(totalSeconds % 60);
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(seconds).padStart(2, "0")}`;
  };

  // Transform data and calculate totals using useMemo
  const { mergedData, totals } = useMemo(() => {
    const result = {};
    const totals = {
      fy1: { total_trains_count: 0, total_total_time: 0, avg_times: [] },
      fy2: { total_trains_count: 0, total_total_time: 0, avg_times: [] },
    };

    if (!Array.isArray(data) || data.length === 0) {
      return { mergedData: result, totals };
    }

    const fy1 = data[0]?.financial_year || "--";
    const fy2 = data[1]?.financial_year || "--";

    data.forEach((yearData) => {
      const fy = yearData?.financial_year;
      if (!fy || !yearData?.children) return;

      // Initialize aggregates for each month
      const monthAggregates = {};
      monthOrder.forEach((month) => {
        monthAggregates[month] = {
          total_trains_count: 0,
          total_total_time: 0,
          avg_times: [],
        };
      });

      // Aggregate across stations
      yearData.children.forEach((stationEntry) => {
        if (stationEntry?.children) {
          stationEntry.children.forEach((monthData) => {
            const month = monthData?.month_name;
            const monthEntry = monthData?.data?.[0];
            if (month && monthEntry) {
              const trains = Number(monthEntry.total_trains_count) || 0;
              const totalTime = Number(monthEntry.total_total_time) || 0;
              const avgTimeSec = timeToSeconds(monthEntry.avg_avg_time);

              monthAggregates[month].total_trains_count += trains;
              monthAggregates[month].total_total_time += totalTime;
              monthAggregates[month].avg_times.push(avgTimeSec);

              // Update totals
              if (fy === fy1) {
                totals.fy1.total_trains_count += trains;
                totals.fy1.total_total_time += totalTime;
                if (avgTimeSec) totals.fy1.avg_times.push(avgTimeSec);
              } else if (fy === fy2) {
                totals.fy2.total_trains_count += trains;
                totals.fy2.total_total_time += totalTime;
                if (avgTimeSec) totals.fy2.avg_times.push(avgTimeSec);
              }
            }
          });
        }
      });

      // Finalize aggregates
      Object.entries(monthAggregates).forEach(([month, agg]) => {
        if (agg.total_trains_count > 0) {
          if (!result[month]) {
            result[month] = {};
          }
          result[month][fy] = {
            total_trains_count: agg.total_trains_count,
            total_total_time: agg.total_total_time,
            avg_avg_time:
              agg.avg_times.length > 0
                ? secondsToHHMMSS(
                    agg.avg_times.reduce((sum, time) => sum + time, 0) /
                      agg.avg_times.length,
                  )
                : "--",
          };
        }
      });
    });

    // Calculate average for avg_avg_time in totals
    totals.fy1.avg_avg_time =
      totals.fy1.avg_times.length > 0
        ? secondsToHHMMSS(
            totals.fy1.avg_times.reduce((sum, time) => sum + time, 0) /
              totals.fy1.avg_times.length,
          )
        : "--";
    totals.fy2.avg_avg_time =
      totals.fy2.avg_times.length > 0
        ? secondsToHHMMSS(
            totals.fy2.avg_times.reduce((sum, time) => sum + time, 0) /
              totals.fy2.avg_times.length,
          )
        : "--";

    return { mergedData: result, totals };
  }, [data]);

  // Early return after hooks
  if (!Array.isArray(data) || data.length === 0) {
    return <p>No data available</p>;
  }

  // Extract financial years with fallback
  const fy1 = data[0]?.financial_year || "--";
  const fy2 = data[1]?.financial_year || "--";

  // Calculate variation percentage with safe division
  const calculateVariation = (current, previous) => {
    if (!current || !previous || current === 0) return null;
    return (((previous - current) / current) * 100).toFixed(2);
  };
  const tdStyle = { width: "10px" };
  return (
    <div className="newUiBody">
      <div className="table-responsive">
        <table
          className="table newUiTable table-striped table-hover table-bordered"
          ref={tableRef}
        >
          <thead>
            <tr className="table-dark text-center">
              <th></th>
              <th colSpan="3">Financial Year ({fy2})</th>
              <th colSpan="3">Financial Year ({fy1})</th>
              <th colSpan="3">
                Variation ({fy2} - {fy1})
              </th>
            </tr>
            <tr className="table-dark text-center">
              <th style={tdStyle}>Month</th>
              <th style={tdStyle}>Total Trains</th>
              <th style={tdStyle}>Avg Time</th>
              <th style={tdStyle}>Total Time</th>
              <th style={tdStyle}>Total Trains</th>
              <th style={tdStyle}>Avg Time</th>
              <th style={tdStyle}>Total Time</th>
              <th style={tdStyle}>Total Trains %</th>
              {/* <th style={tdStyle}>Avg Time %</th> */}
              <th style={tdStyle}>Total Time %</th>
            </tr>
          </thead>
          <tbody>
            {monthOrder
              .filter((month) => mergedData[month])
              .map((month) => {
                const fy1Data = mergedData[month][fy1] || {};
                const fy2Data = mergedData[month][fy2] || {};

                const fy1AvgTimeSec = timeToSeconds(fy1Data.avg_avg_time);
                const fy2AvgTimeSec = timeToSeconds(fy2Data.avg_avg_time);

                // Convert total_total_time (in seconds) to minutes for minutesToHHMM
                const fy1TotalTime = fy1Data.total_total_time
                  ? Math.floor(Number(fy1Data.total_total_time))
                  : null;
                const fy2TotalTime = fy2Data.total_total_time
                  ? Math.floor(Number(fy2Data.total_total_time))
                  : null;

                const avgTimeF1 = (
                  fy1TotalTime / fy1Data.total_trains_count
                ).toFixed(2);
                const avgTimeF2 = (
                  fy2TotalTime / fy2Data.total_trains_count
                ).toFixed(2);
                const variationTotalTrains = calculateVariation(
                  Number(fy2Data.total_trains_count),
                  Number(fy1Data.total_trains_count),
                );
                const variationAvgTime = calculateVariation(
                  fy2AvgTimeSec,
                  fy1AvgTimeSec,
                );
                const variationTotalTime = calculateVariation(
                  Number(fy2Data.total_total_time),
                  Number(fy1Data.total_total_time),
                );

                return (
                  <tr key={month} className="text-center">
                    <td>{month}</td>
                    <td>{fy2Data.total_trains_count ?? "--"}</td>
                    <td>{minutesToHHMM(avgTimeF2) ?? "--"}</td>
                    <td>{fy2TotalTime ? minutesToHHMM(fy2TotalTime) : "--"}</td>
                    <td>{fy1Data.total_trains_count ?? "--"}</td>
                    <td> {minutesToHHMM(avgTimeF1) ?? "--"}</td>
                    <td>{fy1TotalTime ? minutesToHHMM(fy1TotalTime) : "--"}</td>
                    <td style={{ width: "10%" }}>
                      <div
                        className="m-1 text-center"
                        style={{
                          borderRadius: "5px",
                          background:
                            variationTotalTrains > 0
                              ? "rgba(233, 75, 75, 0.9)"
                              : variationTotalTrains < 0
                                ? "rgba(88, 223, 61, 0.78)"
                                : "transparent",
                        }}
                      >
                        {variationTotalTrains ? (
                          variationTotalTrains > 0 ? (
                            <BsFillCaretUpFill />
                          ) : (
                            <BsFillCaretDownFill />
                          )
                        ) : (
                          "--"
                        )}
                        {variationTotalTrains && variationTotalTrains}
                      </div>
                    </td>
                    {/* <td style={{ width: "10%" }}>
                      <div
                        className="m-1 text-center"
                        style={{
                          borderRadius: "5px",
                          background:
                            variationAvgTime > 0
                              ? "rgba(233, 75, 75, 0.9)"
                              : variationAvgTime < 0
                                ? "rgba(88, 223, 61, 0.78)"
                                : "transparent",
                        }}
                      >
                        {variationAvgTime ? (
                          variationAvgTime > 0 ? (
                            <BsFillCaretUpFill />
                          ) : (
                            <BsFillCaretDownFill />
                          )
                        ) : (
                          "--"
                        )}
                        {variationAvgTime && variationAvgTime}
                      </div>
                    </td> */}
                    <td style={{ width: "10%" }}>
                      <div
                        className="m-1 text-center"
                        style={{
                          borderRadius: "5px",
                          background:
                            variationTotalTime > 0
                              ? "rgba(233, 75, 75, 0.9)"
                              : variationTotalTime < 0
                                ? "rgba(88, 223, 61, 0.78)"
                                : "transparent",
                        }}
                      >
                        {variationTotalTime ? (
                          variationTotalTime > 0 ? (
                            <BsFillCaretUpFill />
                          ) : (
                            <BsFillCaretDownFill />
                          )
                        ) : (
                          "--"
                        )}
                        {variationTotalTime && variationTotalTime}
                      </div>
                    </td>
                  </tr>
                );
              })}
            <tr
              className="table-dark text-center"
              style={{ fontWeight: "bold" }}
            >
              <td>Total/Avg</td>
              <td>{totals.fy2.total_trains_count || "--"}</td>
              <td>
                {minutesToHHMM(
                  (
                    totals.fy2.total_total_time / totals.fy2.total_trains_count
                  ).toFixed(2),
                )}
              </td>
              <td>
                {totals.fy2.total_total_time
                  ? minutesToHHMM(Math.floor(totals.fy2.total_total_time))
                  : "--"}
              </td>
              <td>{totals.fy1.total_trains_count || "--"}</td>
              <td>
                {minutesToHHMM(
                  (
                    totals.fy1.total_total_time / totals.fy1.total_trains_count
                  ).toFixed(2),
                )}
              </td>
              <td>
                {totals.fy1.total_total_time
                  ? minutesToHHMM(Math.floor(totals.fy1.total_total_time))
                  : "--"}
              </td>
              <td style={{ width: "10%" }}>
                <div
                  className="m-1 text-center"
                  style={{
                    borderRadius: "5px",
                    background:
                      calculateVariation(
                        totals.fy2.total_trains_count,
                        totals.fy1.total_trains_count,
                      ) > 0
                        ? "rgba(233, 75, 75, 0.9)"
                        : calculateVariation(
                              totals.fy2.total_trains_count,
                              totals.fy1.total_trains_count,
                            ) < 0
                          ? "rgba(88, 223, 61, 0.78)"
                          : "transparent",
                  }}
                >
                  {calculateVariation(
                    totals.fy2.total_trains_count,
                    totals.fy1.total_trains_count,
                  ) ? (
                    calculateVariation(
                      totals.fy2.total_trains_count,
                      totals.fy1.total_trains_count,
                    ) > 0 ? (
                      <BsFillCaretUpFill />
                    ) : (
                      <BsFillCaretDownFill />
                    )
                  ) : null}
                  {calculateVariation(
                    totals.fy2.total_trains_count,
                    totals.fy1.total_trains_count,
                  ) &&
                    calculateVariation(
                      totals.fy2.total_trains_count,
                      totals.fy1.total_trains_count,
                    )}
                </div>
              </td>
              {/* <td style={{ width: "10%" }}>
                <div
                  className="m-1 text-center"
                  style={{
                    borderRadius: "5px",
                    background:
                      calculateVariation(
                        timeToSeconds(totals.fy2.avg_avg_time),
                        timeToSeconds(totals.fy1.avg_avg_time),
                      ) > 0
                        ? "rgba(233, 75, 75, 0.9)"
                        : calculateVariation(
                              timeToSeconds(totals.fy2.avg_avg_time),
                              timeToSeconds(totals.fy1.avg_avg_time),
                            ) < 0
                          ? " rgba(88, 223, 61, 0.78)"
                          : "transparent",
                  }}
                >
                  {calculateVariation(
                    timeToSeconds(totals.fy2.avg_avg_time),
                    timeToSeconds(totals.fy1.avg_avg_time),
                  ) ? (
                    calculateVariation(
                      timeToSeconds(totals.fy2.avg_avg_time),
                      timeToSeconds(totals.fy1.avg_avg_time),
                    ) > 0 ? (
                      <BsFillCaretUpFill />
                    ) : (
                      <BsFillCaretDownFill />
                    )
                  ) : null}
                  {calculateVariation(
                    timeToSeconds(totals.fy2.avg_avg_time),
                    timeToSeconds(totals.fy1.avg_avg_time),
                  ) &&
                    calculateVariation(
                      timeToSeconds(totals.fy2.avg_avg_time),
                      timeToSeconds(totals.fy1.avg_avg_time),
                    )}
                </div>
              </td> */}
              <td style={{ width: "10%" }}>
                <div
                  className="m-1 text-center"
                  style={{
                    borderRadius: "5px",
                    background:
                      calculateVariation(
                        totals.fy2.total_total_time,
                        totals.fy1.total_total_time,
                      ) > 0
                        ? "rgba(233, 75, 75, 0.9)"
                        : calculateVariation(
                              totals.fy2.total_total_time,
                              totals.fy1.total_total_time,
                            ) < 0
                          ? "rgba(88, 223, 61, 0.78)"
                          : "transparent",
                  }}
                >
                  {calculateVariation(
                    totals.fy2.total_total_time,
                    totals.fy1.total_total_time,
                  ) ? (
                    calculateVariation(
                      totals.fy2.total_total_time,
                      totals.fy1.total_total_time,
                    ) > 0 ? (
                      <BsFillCaretUpFill />
                    ) : (
                      <BsFillCaretDownFill />
                    )
                  ) : null}
                  {calculateVariation(
                    totals.fy2.total_total_time,
                    totals.fy1.total_total_time,
                  ) &&
                    calculateVariation(
                      totals.fy2.total_total_time,
                      totals.fy1.total_total_time,
                    )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <DownloadExcel
          tableRef={tableRef}
          fileName={`PDD Summary - ${getCurrentDate()}`}
        />
      </div>
    </div>
  );
};

export default PDDSummaryTable;
